import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, AlertTriangle, X } from "lucide-react";
import { getIncidents, createIncident, updateIncident, getLogements } from "@/lib/api";

const priorityColors = {
  basse: "bg-gray-100 text-gray-700",
  normale: "bg-blue-100 text-blue-700",
  haute: "bg-orange-100 text-orange-700",
  urgente: "bg-red-100 text-red-700"
};

const statusColors = {
  signale: "bg-yellow-100 text-yellow-700",
  en_cours: "bg-blue-100 text-blue-700",
  resolu: "bg-green-100 text-green-700"
};

export default function IncidentsPage() {
  const [incidents, setIncidents] = useState([]);
  const [logements, setLogements] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    logement_id: "",
    description: "",
    date_signalement: new Date().toISOString().split('T')[0],
    priorite: "normale"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [incRes, logRes] = await Promise.all([
        getIncidents(),
        getLogements()
      ]);
      setIncidents(incRes.data);
      
      const logMap = {};
      logRes.data.forEach(l => logMap[l.id] = l);
      setLogements(logMap);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createIncident(formData);
      toast.success("Incident signalé !");
      setShowModal(false);
      setFormData({
        logement_id: "",
        description: "",
        date_signalement: new Date().toISOString().split('T')[0],
        priorite: "normale"
      });
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleStatusChange = async (id, newStatus, cout = null) => {
    try {
      const data = { statut: newStatus };
      if (cout !== null) data.cout_reparation = cout;
      await updateIncident(id, data);
      toast.success("Incident mis à jour !");
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="incidents-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-display text-[#004333]">Incidents & Maintenance</h2>
          <p className="text-gray-500">{incidents.length} incident(s)</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-incident-btn"
        >
          <Plus className="w-4 h-4" />
          Signaler un incident
        </button>
      </div>

      {incidents.length > 0 ? (
        <div className="space-y-4">
          {incidents.map((incident) => (
            <div key={incident.id} className="bg-white border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-[#004333]">
                    {logements[incident.logement_id]?.titre || "Logement inconnu"}
                  </h3>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-2 py-1 text-xs font-medium ${priorityColors[incident.priorite]}`}>
                      {incident.priorite}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium ${statusColors[incident.statut]}`}>
                      {incident.statut}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-500">{incident.date_signalement}</div>
              </div>
              
              <p className="text-gray-600 mb-4">{incident.description}</p>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-4">
                  <select
                    value={incident.statut}
                    onChange={(e) => handleStatusChange(incident.id, e.target.value)}
                    className="input-hh text-sm py-2"
                  >
                    <option value="signale">Signalé</option>
                    <option value="en_cours">En cours</option>
                    <option value="resolu">Résolu</option>
                  </select>
                  
                  {incident.statut === "resolu" && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Coût:</span>
                      <input
                        type="number"
                        value={incident.cout_reparation || 0}
                        onChange={(e) => handleStatusChange(incident.id, incident.statut, parseFloat(e.target.value))}
                        className="input-hh w-24 text-sm py-2"
                      />
                      <span className="text-sm text-gray-500">€</span>
                    </div>
                  )}
                </div>
                
                {incident.cout_reparation > 0 && (
                  <div className="font-mono font-bold text-[#C9922A]">
                    {incident.cout_reparation} €
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-16 text-center">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-[#004333] mb-2">Aucun incident</h3>
          <p className="text-gray-500">Tout va bien ! Les incidents apparaîtront ici.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">Signaler un incident</h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Logement concerné</label>
                <select
                  required
                  value={formData.logement_id}
                  onChange={(e) => setFormData({ ...formData, logement_id: e.target.value })}
                  className="input-hh"
                >
                  <option value="">Sélectionner un logement</option>
                  {Object.values(logements).map((log) => (
                    <option key={log.id} value={log.id}>{log.titre}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description de l'incident</label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-hh"
                  placeholder="Décrivez le problème..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de signalement</label>
                  <input
                    type="date"
                    required
                    value={formData.date_signalement}
                    onChange={(e) => setFormData({ ...formData, date_signalement: e.target.value })}
                    className="input-hh"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                  <select
                    value={formData.priorite}
                    onChange={(e) => setFormData({ ...formData, priorite: e.target.value })}
                    className="input-hh"
                  >
                    <option value="basse">Basse</option>
                    <option value="normale">Normale</option>
                    <option value="haute">Haute</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Signaler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
